<!-- ── Loading ────────────────────────────────────────────────────────── -->
<div *ngIf="isPageLoading" class="trv-loading">
  <mat-spinner diameter="40"></mat-spinner>
</div>

<!-- ── Page ────────────────────────────────────────────────────────────── -->
<div *ngIf="!isPageLoading" class="wizard-page">

  <div class="wizard-header">
    <button mat-icon-button type="button" (click)="router.navigate(['/requisitions'])" matTooltip="Back to Requisitions">
      <mat-icon>arrow_back</mat-icon>
    </button>
    <h2 class="wizard-title">Requisition Review</h2>
    <span class="trv-doc-chip" *ngIf="enquiryId">{{ enquiryId }}</span>
  </div>

  <mat-card class="wizard-card">
    <mat-horizontal-stepper [linear]="false" #stepper>

      <!-- ══════════════════════════════════════════════════════════════ -->
      <!-- Step 1 — Requisition Details (read-only)                       -->
      <!-- ══════════════════════════════════════════════════════════════ -->
      <mat-step [stepControl]="detailsForm">
        <ng-template matStepLabel>
          <span matTooltip="Links this requisition to a specific Class and Customer so it can be tracked, scheduled and billed correctly"
                matTooltipPosition="below">Requisition Details</span>
        </ng-template>

        <form [formGroup]="detailsForm" class="step-form">

          <!-- Enquiry No. -->
          <mat-form-field appearance="outline" class="form-field-full">
            <mat-label>Enquiry No.</mat-label>
            <mat-icon matPrefix>tag</mat-icon>
            <input matInput [value]="enquiryId" readonly>
          </mat-form-field>

          <!-- Customer Type -->
          <mat-form-field appearance="outline" class="form-field-full">
            <mat-label>Customer Type</mat-label>
            <mat-icon matPrefix>category</mat-icon>
            <input matInput [value]="customerType" readonly>
          </mat-form-field>

          <!-- Customer Name -->
          <mat-form-field appearance="outline" class="form-field-full">
            <mat-label>Customer Name</mat-label>
            <mat-icon matPrefix>business</mat-icon>
            <input matInput [value]="customerName" readonly>
          </mat-form-field>

          <!-- Customer info card (mirrors wizard's .customer-info-card) -->
          <ng-container *ngIf="customerDetails">
            <div class="customer-info-card">
              <div class="ci-row">
                <div class="ci-item">
                  <span class="ci-label"><mat-icon class="ci-icon">location_on</mat-icon>Address</span>
                  <span class="ci-value">{{ customerDetails.Address ?? customerDetails.address || '—' }}</span>
                </div>
                <div class="ci-item">
                  <span class="ci-label"><mat-icon class="ci-icon">receipt</mat-icon>GST No.</span>
                  <span class="ci-value">{{ customerDetails.GstNumber ?? customerDetails.gstNumber || '—' }}</span>
                </div>
                <div class="ci-item">
                  <span class="ci-label"><mat-icon class="ci-icon">language</mat-icon>Website</span>
                  <span class="ci-value">{{ customerDetails.Website ?? customerDetails.website || '—' }}</span>
                </div>
              </div>
              <div class="ci-row">
                <div class="ci-item">
                  <span class="ci-label"><mat-icon class="ci-icon">person</mat-icon>Contact Person</span>
                  <span class="ci-value">
                    {{ customerDetails.ContactPerson?.Name ?? customerDetails.ContactPerson?.name
                       ?? customerDetails.contactPerson?.Name ?? customerDetails.contactPerson?.name || '—' }}
                  </span>
                </div>
                <div class="ci-item">
                  <span class="ci-label"><mat-icon class="ci-icon">email</mat-icon>Email</span>
                  <span class="ci-value">
                    {{ customerDetails.ContactPerson?.Email ?? customerDetails.ContactPerson?.email
                       ?? customerDetails.contactPerson?.Email ?? customerDetails.contactPerson?.email || '—' }}
                  </span>
                </div>
                <div class="ci-item">
                  <span class="ci-label"><mat-icon class="ci-icon">phone</mat-icon>Contact No.</span>
                  <span class="ci-value">
                    {{ customerDetails.ContactPerson?.Mobile ?? customerDetails.ContactPerson?.mobile
                       ?? customerDetails.contactPerson?.Mobile ?? customerDetails.contactPerson?.mobile || '—' }}
                  </span>
                </div>
              </div>
            </div>
          </ng-container>

        </form>

        <div class="step-actions">
          <span></span>
          <button mat-flat-button color="primary" matStepperNext>Next</button>
        </div>
      </mat-step>

      <!-- ══════════════════════════════════════════════════════════════ -->
      <!-- Step 2 — Batch Schedule (read-only)                            -->
      <!-- ══════════════════════════════════════════════════════════════ -->
      <mat-step [stepControl]="scheduleForm">
        <ng-template matStepLabel>
          <span matTooltip="Session slots define when and where training happens — accurate dates ensure correct VM provisioning windows"
                matTooltipPosition="below">Batch Schedule</span>
        </ng-template>

        <form [formGroup]="scheduleForm" class="step-form">

          <!-- Batch Title -->
          <mat-form-field appearance="outline" class="form-field-full">
            <mat-label>Batch Title</mat-label>
            <mat-icon matPrefix>label</mat-icon>
            <input matInput [value]="batchTitle" readonly>
          </mat-form-field>

          <!-- Trainer -->
          <mat-form-field appearance="outline" class="form-field-full">
            <mat-label>Trainer</mat-label>
            <mat-icon matPrefix>person_pin</mat-icon>
            <input matInput [value]="batchTrainer || '— None —'" readonly>
          </mat-form-field>

          <!-- Session Slots — read-only accordion, mirrors the wizard's slot accordion -->
          <div class="date-ranges-section">
            <div class="section-sub-heading-row">
              <span class="section-sub-heading">Session Slots</span>
            </div>

            <mat-accordion class="slot-accordion">
              <mat-expansion-panel
                *ngFor="let dr of reviewDateRanges; let i = index"
                class="slot-panel"
                [expanded]="false">

                <mat-expansion-panel-header>
                  <mat-panel-title class="slot-panel-title">
                    <mat-icon class="slot-panel-icon">calendar_today</mat-icon>
                    <span class="slot-title-text">{{ dr.slotTitle || ('Slot ' + (i + 1)) }}</span>
                    <span class="slot-panel-header-summary" *ngIf="dr.startDate">
                      &mdash; {{ dr.startDate | date:'d MMM' }} to {{ dr.endDate | date:'d MMM yyyy' }}
                      <span class="slot-header-times" *ngIf="dr.startTime">
                        &middot; {{ formatTime12h(dr.startTime) }}&ndash;{{ formatTime12h(dr.endTime) }}
                      </span>
                    </span>
                  </mat-panel-title>
                </mat-expansion-panel-header>

                <div class="slot-panel-body">
                  <mat-form-field appearance="outline" class="date-range-field">
                    <mat-label>Date Range</mat-label>
                    <mat-icon matPrefix>date_range</mat-icon>
                    <input matInput
                           [value]="(dr.startDate | date:'d MMM yyyy') + ' – ' + (dr.endDate | date:'d MMM yyyy')"
                           readonly>
                  </mat-form-field>
                  <div class="times-row">
                    <mat-form-field appearance="outline" class="time-field">
                      <mat-label>Start Time</mat-label>
                      <input matInput [value]="formatTime12h(dr.startTime)" readonly>
                    </mat-form-field>
                    <mat-form-field appearance="outline" class="time-field">
                      <mat-label>End Time</mat-label>
                      <input matInput [value]="formatTime12h(dr.endTime)" readonly>
                    </mat-form-field>
                  </div>
                </div>

              </mat-expansion-panel>
            </mat-accordion>

            <p class="trainer-hint" *ngIf="reviewDateRanges.length === 0">No session slots defined for this requisition.</p>
          </div>

        </form>

        <div class="step-actions">
          <button mat-button matStepperPrevious>Back</button>
          <button mat-flat-button color="primary" matStepperNext>Next</button>
        </div>
      </mat-step>

      <!-- ══════════════════════════════════════════════════════════════ -->
      <!-- Step 3 — Additional Information (read-only)                    -->
      <!-- ══════════════════════════════════════════════════════════════ -->
      <mat-step [stepControl]="additionalForm">
        <ng-template matStepLabel>
          <span matTooltip="Extra context helps coordinators and trainers action the requisition without needing to follow up separately"
                matTooltipPosition="below">Additional Info</span>
        </ng-template>

        <form [formGroup]="additionalForm" class="step-form">
          <mat-form-field appearance="outline" class="form-field-full">
            <mat-label>Special Requirements</mat-label>
            <mat-icon matPrefix>report_problem</mat-icon>
            <textarea matInput [value]="specialRequirements" rows="4" readonly
                      placeholder="—"></textarea>
          </mat-form-field>

          <mat-form-field appearance="outline" class="form-field-full">
            <mat-label>Final Comments</mat-label>
            <mat-icon matPrefix>comment</mat-icon>
            <textarea matInput [value]="finalComments" rows="4" readonly
                      placeholder="—"></textarea>
          </mat-form-field>
        </form>

        <div class="step-actions">
          <button mat-button matStepperPrevious>Back</button>
          <button mat-flat-button color="primary" matStepperNext>Next</button>
        </div>
      </mat-step>

      <!-- ══════════════════════════════════════════════════════════════ -->
      <!-- Step 4 — Template Selection (editable)                         -->
      <!-- ══════════════════════════════════════════════════════════════ -->
      <mat-step [stepControl]="templateForm">
        <ng-template matStepLabel>
          <span matTooltip="Attach the VM and User Account templates that will be provisioned for this training batch"
                matTooltipPosition="below">Template Selection</span>
        </ng-template>

        <form [formGroup]="templateForm" class="step-form">

          <!-- VM Template -->
          <mat-form-field appearance="outline" class="form-field-full">
            <mat-label>VM Template *</mat-label>
            <mat-icon matPrefix>dns</mat-icon>
            <input matInput
                   [(ngModel)]="vmTemplateQuery"
                   [ngModelOptions]="{standalone: true}"
                   [matAutocomplete]="vmAuto"
                   (ngModelChange)="onVmTemplateSearch($event)"
                   (focus)="onVmTemplateSearch('')"
                   placeholder="Search VM template…"
                   autocomplete="off">
            <button *ngIf="selectedVmTemplate" matSuffix mat-icon-button type="button"
                    (click)="clearVmTemplate()" matTooltip="Clear">
              <mat-icon>close</mat-icon>
            </button>
            <mat-autocomplete #vmAuto="matAutocomplete"
                              [displayWith]="displayVmFn"
                              (optionSelected)="onVmTemplateSelected($event)">
              <mat-option *ngFor="let t of vmTemplateResults" [value]="t">{{ t.name }}</mat-option>
              <mat-option *ngIf="vmTemplateResults.length === 0 && vmTemplateQuery.length > 0" disabled>
                No templates match "{{ vmTemplateQuery }}"
              </mat-option>
            </mat-autocomplete>
          </mat-form-field>

          <!-- Selected VM preview accordion -->
          <mat-accordion class="vm-preview-accordion" *ngIf="selectedVmTemplate">
            <mat-expansion-panel class="vm-preview-panel" [expanded]="false">
              <mat-expansion-panel-header>
                <mat-panel-title class="vp-panel-title">
                  <mat-icon class="vp-panel-icon">description</mat-icon>
                  Template Details
                </mat-panel-title>
              </mat-expansion-panel-header>
              <div class="vp-grid">
                <span class="vp-label"><mat-icon class="vp-label-icon">label</mat-icon>Name</span>
                <span class="vp-value">{{ selectedVmTemplate.name }}</span>
                <span class="vp-label"><mat-icon class="vp-label-icon">article</mat-icon>Description</span>
                <span class="vp-value">{{ selectedVmTemplate.description || '—' }}</span>
                <span class="vp-label"><mat-icon class="vp-label-icon">toggle_on</mat-icon>Status</span>
                <span class="vp-value">{{ selectedVmTemplate.status || '—' }}</span>
              </div>
            </mat-expansion-panel>

            <mat-expansion-panel class="vm-preview-panel" [expanded]="false">
              <mat-expansion-panel-header>
                <mat-panel-title class="vp-panel-title">
                  <mat-icon class="vp-panel-icon">cloud</mat-icon>
                  Cloud Provider
                </mat-panel-title>
              </mat-expansion-panel-header>
              <div class="vp-grid">
                <span class="vp-label"><mat-icon class="vp-label-icon">corporate_fare</mat-icon>Provider</span>
                <span class="vp-value">{{ selectedVmTemplate.cloudProvider || '—' }}</span>
                <span class="vp-label"><mat-icon class="vp-label-icon">desktop_windows</mat-icon>OS</span>
                <span class="vp-value">{{ selectedVmTemplate.os || '—' }}</span>
                <ng-container *ngIf="selectedVmTemplate.osVersion">
                  <span class="vp-label"><mat-icon class="vp-label-icon">featured_play_list</mat-icon>Version</span>
                  <span class="vp-value">{{ selectedVmTemplate.osVersion }}</span>
                </ng-container>
                <span class="vp-label"><mat-icon class="vp-label-icon">location_on</mat-icon>Region</span>
                <span class="vp-value">{{ selectedVmTemplate.region || '—' }}</span>
                <span class="vp-label"><mat-icon class="vp-label-icon">memory</mat-icon>VM Size</span>
                <span class="vp-value">{{ selectedVmTemplate.size || '—' }}</span>
                <ng-container *ngIf="selectedVmTemplate.vCpu">
                  <span class="vp-label"><mat-icon class="vp-label-icon">developer_board</mat-icon>vCPUs</span>
                  <span class="vp-value">{{ selectedVmTemplate.vCpu }}</span>
                </ng-container>
                <ng-container *ngIf="selectedVmTemplate.ramGb">
                  <span class="vp-label"><mat-icon class="vp-label-icon">storage</mat-icon>RAM</span>
                  <span class="vp-value">{{ selectedVmTemplate.ramGb }}</span>
                </ng-container>
                <ng-container *ngIf="selectedVmTemplate.storageGb">
                  <span class="vp-label"><mat-icon class="vp-label-icon">folder</mat-icon>Storage</span>
                  <span class="vp-value">{{ selectedVmTemplate.storageGb }}</span>
                </ng-container>
              </div>
            </mat-expansion-panel>

            <mat-expansion-panel class="vm-preview-panel" [expanded]="false">
              <mat-expansion-panel-header>
                <mat-panel-title class="vp-panel-title">
                  <mat-icon class="vp-panel-icon">apps</mat-icon>
                  Software
                </mat-panel-title>
              </mat-expansion-panel-header>
              <ng-container *ngIf="(selectedVmTemplate.sdks?.length || selectedVmTemplate.databases?.length || selectedVmTemplate.editors?.length || selectedVmTemplate.misc?.length); else noVmSoftware">
                <div *ngIf="selectedVmTemplate.sdks?.length" class="vp-software-group">
                  <span class="vp-sw-label"><mat-icon class="vp-sw-icon">code</mat-icon>SDKs</span>
                  <div class="vp-chips">
                    <span *ngFor="let sdk of selectedVmTemplate.sdks" class="vp-chip">{{ sdk }}</span>
                  </div>
                </div>
                <div *ngIf="selectedVmTemplate.databases?.length" class="vp-software-group">
                  <span class="vp-sw-label"><mat-icon class="vp-sw-icon">dns</mat-icon>Databases</span>
                  <div class="vp-chips">
                    <span *ngFor="let db of selectedVmTemplate.databases" class="vp-chip">{{ db }}</span>
                  </div>
                </div>
                <div *ngIf="selectedVmTemplate.editors?.length" class="vp-software-group">
                  <span class="vp-sw-label"><mat-icon class="vp-sw-icon">build</mat-icon>Dev Tools</span>
                  <div class="vp-chips">
                    <span *ngFor="let ed of selectedVmTemplate.editors" class="vp-chip">{{ ed }}</span>
                  </div>
                </div>
                <div *ngIf="selectedVmTemplate.misc?.length" class="vp-software-group">
                  <span class="vp-sw-label"><mat-icon class="vp-sw-icon">apps</mat-icon>Misc</span>
                  <div class="vp-chips">
                    <span *ngFor="let sw of selectedVmTemplate.misc" class="vp-chip">{{ sw }}</span>
                  </div>
                </div>
              </ng-container>
              <ng-template #noVmSoftware>
                <p class="vp-none">No software listed for this template.</p>
              </ng-template>
            </mat-expansion-panel>
          </mat-accordion>

          <!-- UA Template -->
          <mat-form-field appearance="outline" class="form-field-full">
            <mat-label>User Account Template *</mat-label>
            <mat-icon matPrefix>manage_accounts</mat-icon>
            <input matInput
                   [(ngModel)]="uaTemplateQuery"
                   [ngModelOptions]="{standalone: true}"
                   [matAutocomplete]="uaAuto"
                   (ngModelChange)="onUaTemplateSearch($event)"
                   (focus)="onUaTemplateSearch('')"
                   placeholder="Search user account template…"
                   autocomplete="off">
            <button *ngIf="selectedUaTemplate" matSuffix mat-icon-button type="button"
                    (click)="clearUaTemplate()" matTooltip="Clear">
              <mat-icon>close</mat-icon>
            </button>
            <mat-autocomplete #uaAuto="matAutocomplete"
                              [displayWith]="displayUaFn"
                              (optionSelected)="onUaTemplateSelected($event)">
              <mat-option *ngFor="let t of uaTemplateResults" [value]="t">{{ t.name }}</mat-option>
              <mat-option *ngIf="uaTemplateResults.length === 0 && uaTemplateQuery.length > 0" disabled>
                No templates match "{{ uaTemplateQuery }}"
              </mat-option>
            </mat-autocomplete>
          </mat-form-field>

          <!-- Selected UA preview card -->
          <mat-accordion class="vm-preview-accordion" *ngIf="selectedUaTemplate">
            <mat-expansion-panel class="vm-preview-panel" [expanded]="true">
              <mat-expansion-panel-header>
                <mat-panel-title class="vp-panel-title">
                  <mat-icon class="vp-panel-icon">description</mat-icon>
                  Template Details
                </mat-panel-title>
              </mat-expansion-panel-header>
              <div class="vp-grid">
                <span class="vp-label"><mat-icon class="vp-label-icon">label</mat-icon>Name</span>
                <span class="vp-value">{{ selectedUaTemplate.name }}</span>
                <span class="vp-label"><mat-icon class="vp-label-icon">article</mat-icon>Description</span>
                <span class="vp-value">{{ selectedUaTemplate.description || '—' }}</span>
              </div>
            </mat-expansion-panel>
          </mat-accordion>

        </form>

        <div class="step-actions">
          <button mat-button matStepperPrevious>Back</button>
          <button mat-flat-button color="primary" matStepperNext [disabled]="!hasAtLeastOneTemplate">
            Next
          </button>
        </div>
      </mat-step>

      <!-- ══════════════════════════════════════════════════════════════ -->
      <!-- Step 5 — Review (read-only summary)                             -->
      <!-- ══════════════════════════════════════════════════════════════ -->
      <mat-step [stepControl]="reviewForm">
        <ng-template matStepLabel>
          <span matTooltip="Once forwarded, the requisition enters the processing queue — verify everything is accurate before submitting"
                matTooltipPosition="below">Review</span>
        </ng-template>

        <!-- ── Review accordion — same structure as create wizard ── -->
        <mat-accordion class="review-accordion" [multi]="false">

          <!-- Panel A: Requisition Details -->
          <mat-expansion-panel #panelA class="review-panel" [expanded]="false">
            <mat-expansion-panel-header>
              <mat-panel-title class="review-panel-title">
                <mat-icon class="review-panel-icon">list_alt</mat-icon>
                Requisition Details
              </mat-panel-title>
              <mat-panel-description *ngIf="!panelA.expanded">
                <div class="review-panel-summary">
                  <span class="rps-item" *ngIf="enquiryId"><mat-icon class="rps-icon">tag</mat-icon>{{ enquiryId }}</span>
                  <span class="rps-item" *ngIf="customerName"><mat-icon class="rps-icon">business</mat-icon>{{ customerName }}</span>
                  <span class="rps-item" *ngIf="customerType"><mat-icon class="rps-icon">category</mat-icon>{{ customerType }}</span>
                </div>
              </mat-panel-description>
            </mat-expansion-panel-header>
            <div class="review-grid">
              <span class="review-label"><mat-icon class="rl-icon">tag</mat-icon>Enquiry No.</span>
              <span class="review-value">{{ enquiryId || '—' }}</span>
              <span class="review-label"><mat-icon class="rl-icon">receipt_long</mat-icon>Requisition No.</span>
              <span class="review-value">{{ docNo || '—' }}</span>
              <span class="review-label"><mat-icon class="rl-icon">category</mat-icon>Customer Type</span>
              <span class="review-value">{{ customerType || '—' }}</span>

              <span class="review-label"><mat-icon class="rl-icon">business</mat-icon>Customer</span>
              <span class="review-value">{{ customerName || '—' }}</span>
              <span class="review-label"><mat-icon class="rl-icon">receipt_long</mat-icon>GST No.</span>
              <span class="review-value">{{ customerDetails?.GstNumber ?? customerDetails?.gstNumber || '—' }}</span>

              <span class="review-label"><mat-icon class="rl-icon">person</mat-icon>Contact Person</span>
              <span class="review-value">
                {{ customerDetails?.ContactPerson?.Name ?? customerDetails?.ContactPerson?.name
                   ?? customerDetails?.contactPerson?.Name ?? customerDetails?.contactPerson?.name || '—' }}
              </span>
              <span class="review-label"><mat-icon class="rl-icon">phone</mat-icon>Contact No.</span>
              <span class="review-value">
                {{ customerDetails?.ContactPerson?.Mobile ?? customerDetails?.ContactPerson?.mobile
                   ?? customerDetails?.contactPerson?.Mobile ?? customerDetails?.contactPerson?.mobile || '—' }}
              </span>

              <span class="review-label"><mat-icon class="rl-icon">alternate_email</mat-icon>Email</span>
              <span class="review-value" style="grid-column: span 3">
                {{ customerDetails?.ContactPerson?.Email ?? customerDetails?.ContactPerson?.email
                   ?? customerDetails?.contactPerson?.Email ?? customerDetails?.contactPerson?.email || '—' }}
              </span>
            </div>
          </mat-expansion-panel>

          <!-- Panel B: Batch Schedule -->
          <mat-expansion-panel #panelB class="review-panel" [expanded]="false">
            <mat-expansion-panel-header>
              <mat-panel-title class="review-panel-title">
                <mat-icon class="review-panel-icon">calendar_month</mat-icon>
                Batch Schedule
              </mat-panel-title>
              <mat-panel-description *ngIf="!panelB.expanded">
                <div class="review-panel-summary">
                  <span class="rps-item" *ngIf="batchTitle"><mat-icon class="rps-icon">label</mat-icon>{{ batchTitle }}</span>
                  <span class="rps-item" *ngIf="batchTrainer"><mat-icon class="rps-icon">person_pin</mat-icon>{{ batchTrainer }}</span>
                  <span class="rps-item"><mat-icon class="rps-icon">calendar_today</mat-icon>{{ reviewDateRanges.length }} slot{{ reviewDateRanges.length !== 1 ? 's' : '' }}</span>
                </div>
              </mat-panel-description>
            </mat-expansion-panel-header>
            <div class="review-grid">
              <span class="review-label"><mat-icon class="rl-icon">label</mat-icon>Batch Title</span>
              <span class="review-value">{{ batchTitle || '—' }}</span>
              <span class="review-label"><mat-icon class="rl-icon">person_pin</mat-icon>Trainer</span>
              <span class="review-value">{{ batchTrainer || '— None —' }}</span>
            </div>
            <div class="slot-review-list" *ngIf="reviewDateRanges.length > 0">
              <div *ngFor="let r of reviewDateRanges; let i = index" class="slot-review-row">
                <mat-icon>event</mat-icon>
                <span>{{ r.slotTitle || ('Slot ' + (i + 1)) }}: {{ r.startDate | date:'d MMM' }} – {{ r.endDate | date:'d MMM yyyy' }}</span>
                <span class="slot-time">{{ formatTime12h(r.startTime) }} – {{ formatTime12h(r.endTime) }}</span>
              </div>
            </div>
          </mat-expansion-panel>

          <!-- Panel C: Additional Information -->
          <mat-expansion-panel #panelC class="review-panel" [expanded]="false">
            <mat-expansion-panel-header>
              <mat-panel-title class="review-panel-title">
                <mat-icon class="review-panel-icon">sticky_note_2</mat-icon>
                Additional Information
              </mat-panel-title>
              <mat-panel-description *ngIf="!panelC.expanded">
                <div class="review-panel-summary">
                  <span class="rps-item" *ngIf="specialRequirements"><mat-icon class="rps-icon">report_problem</mat-icon>Special requirements</span>
                  <span class="rps-item" *ngIf="!specialRequirements && !finalComments">No additional info</span>
                </div>
              </mat-panel-description>
            </mat-expansion-panel-header>
            <div class="review-grid">
              <span class="review-label"><mat-icon class="rl-icon">report_problem</mat-icon>Special Requirements</span>
              <span class="review-value" style="white-space: pre-wrap; max-width: unset; grid-column: span 3">{{ specialRequirements || '—' }}</span>
              <span class="review-label"><mat-icon class="rl-icon">comment</mat-icon>Final Comments</span>
              <span class="review-value" style="white-space: pre-wrap; max-width: unset; grid-column: span 3">{{ finalComments || '—' }}</span>
            </div>
          </mat-expansion-panel>

          <!-- Panel D: Templates (new) -->
          <mat-expansion-panel #panelD class="review-panel" [expanded]="true">
            <mat-expansion-panel-header>
              <mat-panel-title class="review-panel-title">
                <mat-icon class="review-panel-icon">layers</mat-icon>
                Templates
              </mat-panel-title>
              <mat-panel-description *ngIf="!panelD.expanded">
                <div class="review-panel-summary">
                  <span class="rps-item" *ngIf="selectedVmTemplate"><mat-icon class="rps-icon">dns</mat-icon>{{ selectedVmTemplate.name }}</span>
                  <span class="rps-item" *ngIf="selectedUaTemplate"><mat-icon class="rps-icon">manage_accounts</mat-icon>{{ selectedUaTemplate.name }}</span>
                </div>
              </mat-panel-description>
            </mat-expansion-panel-header>
            <div class="review-grid">
              <span class="review-label"><mat-icon class="rl-icon">dns</mat-icon>VM Template</span>
              <span class="review-value">{{ selectedVmTemplate?.name || '—' }}</span>
              <span class="review-label"><mat-icon class="rl-icon">manage_accounts</mat-icon>UA Template</span>
              <span class="review-value">{{ selectedUaTemplate?.name || '—' }}</span>
            </div>
          </mat-expansion-panel>

        </mat-accordion>

        <div class="step-actions">
          <button mat-button matStepperPrevious>Back</button>
          <button mat-flat-button color="primary" matStepperNext [disabled]="!hasAtLeastOneTemplate">
            Review &amp; Forward
          </button>
        </div>

      </mat-step>

      <!-- ══════════════════════════════════════════════════════════════ -->
      <!-- Step 6 — Forward                                                 -->
      <!-- ══════════════════════════════════════════════════════════════ -->
      <mat-step [stepControl]="forwardForm">
        <ng-template matStepLabel>
          <span matTooltip="Route the requisition to the Cloud Settings team — select the recipient and add any handover remarks"
                matTooltipPosition="below">Forward</span>
        </ng-template>

        <form [formGroup]="forwardForm" class="step-form">

          <!-- Destination badge -->
          <div class="fwd-dest-row">
            <mat-icon class="fwd-dest-icon">forward</mat-icon>
            <span class="fwd-dest-label">Forwarding requisition</span>
            <span class="activity-badge fwd-activity-badge">→ Cloud Settings</span>
          </div>

          <!-- Team -->
          <mat-form-field appearance="outline" class="form-field-full">
            <mat-label>Team *</mat-label>
            <mat-icon matPrefix>groups</mat-icon>
            <mat-select formControlName="team">
              <mat-option *ngFor="let t of forwardTeams" [value]="t.team">{{ t.team }}</mat-option>
            </mat-select>
            <mat-hint *ngIf="forwardTeams.length === 0">Loading teams…</mat-hint>
            <mat-error>Team is required.</mat-error>
          </mat-form-field>

          <!-- User -->
          <mat-form-field appearance="outline" class="form-field-full">
            <mat-label>Assign To *</mat-label>
            <mat-icon matPrefix>person_search</mat-icon>
            <mat-select formControlName="user" [disabled]="!forwardForm.value.team">
              <mat-option *ngFor="let u of forwardUsers" [value]="u.name">{{ u.name }}</mat-option>
            </mat-select>
            <mat-hint *ngIf="!forwardForm.value.team">Select a team first</mat-hint>
            <mat-error>User is required.</mat-error>
          </mat-form-field>

          <!-- Remarks -->
          <mat-form-field appearance="outline" class="form-field-full">
            <mat-label>Remarks *</mat-label>
            <mat-icon matPrefix>comment</mat-icon>
            <textarea matInput formControlName="remarks" rows="3"
                      placeholder="Add any remarks or instructions…"></textarea>
            <mat-error>Remarks are required.</mat-error>
          </mat-form-field>

        </form>

        <div class="step-actions">
          <button mat-button matStepperPrevious>Back</button>
          <button mat-flat-button color="primary"
                  (click)="onForward()"
                  [disabled]="isForwarding || forwardForm.invalid">
            <mat-progress-spinner *ngIf="isForwarding"
                                  mode="indeterminate"
                                  diameter="18"
                                  class="btn-spinner">
            </mat-progress-spinner>
            {{ isForwarding ? 'Forwarding…' : 'Forward' }}
          </button>
        </div>

      </mat-step>

    </mat-horizontal-stepper>
  </mat-card>
</div>


